import type { TextUpdatePanelFragment } from "../../client/generated";
import { useTranslation } from "react-i18next";
import { AttributePanel } from "../AttributePanel";
import { TextFormField } from "./TextFormField";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import { Strike } from "@tiptap/extension-strike";
import { Underline } from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { AccessLoading } from "@acdc2/ui/components/access-loading";
import { useRichTextEditor } from "../../rich-text/useRichTextEditor";
import { Language } from "../../rich-text/extension-language";
import {
  useDeleteTextFromPanelMutation,
  useUpdateTextFromPanelMutation,
} from "../../client/generated";

type Props = {
  fragment: TextUpdatePanelFragment;
};

export function TextUpdatePanel({ fragment }: Props) {
  const { t } = useTranslation();
  const [updateAttribute] = useUpdateTextFromPanelMutation();
  const [deleteAttribute] = useDeleteTextFromPanelMutation();
  const editor = useRichTextEditor({
    options: {
      extensions: [
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        Strike,
        Underline,
        Language,
        TextStyle,
        Color,
      ],
      content: fragment.text,
    },
  });

  const onSave = async () => {
    if (!editor) return;

    await updateAttribute({
      variables: {
        attributeId: fragment.id,
        text: editor.getHTML(),
      },
    });
  };

  const onDelete = async () => {
    await deleteAttribute({
      variables: { attributeId: fragment.id },
      update(cache) {
        const id = cache.identify(fragment);
        cache.evict({ id });
        cache.gc();
      },
    });
  };

  if (!editor) {
    return <AccessLoading />;
  }

  return (
    <AttributePanel
      title={t("TextUpdatePanel.title")}
      unsaved={editor?.getHTML() !== fragment.text}
      onSubmit={onSave}
      onDelete={onDelete}
    >
      <TextFormField fragment={fragment.region} editor={editor} />
    </AttributePanel>
  );
}
